"use client";
import { SuccessIcon } from "@shared/ui.icons";
import { Button, Link, Typography } from "@shared/ui.primitives";
import Markdown from "markdown-to-jsx";
import Image from "next/image";
import { useEffect } from "react";
import { useInView } from "react-intersection-observer";

import type { ProgressBarProps } from "../ProgressBar/ProgressBar";
import { ProgressBar } from "../ProgressBar/ProgressBar";

import * as styles from "./PromoTile.css";
import { IMAGE_SIZE_OPTIONS } from "./constants";

export type PromoTileVariant = "standard" | "slim" | "compact";
export type PromoTileLayout = "padding" | "full";
export type LinkAlignment = "start" | "end";

type Digit = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
export type ZeroToOne =
  | "0"
  | `0.${Digit}`
  | `0.${Digit}${Digit}`
  | "1";



type PromoTileImage = {
  src: string;
  altText: string;
};

export type Threshold = { value: number, reward?: string; isGift?: boolean };

export type PromoTileProgress = {
  current: number;
  threshold: Threshold | [Threshold, Threshold, ...Threshold[]];
};

export type PromoTileProps = {
  title: string;
  description: string;
  buttonText?: string;
  onClick?: () => void;
  href?: string;
  target?: "_self" | "_blank" | "_parent" | "_top";
  image?: PromoTileImage;
  progress?: PromoTileProgress | undefined;
  successIcon?: boolean;
  linkAsButton?: boolean;
  /**
   * The size variant of the PromoTile.
   * 'standard': 104px height
   * 'slim': 60px height
   * 'compact': 104px height
   * @default 'standard'
   * @type {'standard' | 'slim' | 'compact' }
   */
  variant?: PromoTileVariant;
  /**
   * The layout style of the PromoTile.
   * 'padding': Adds padding around the content
   * 'full': Full bleed image with no padding
   * @default 'padding'
   *  @type { 'padding' | 'full' }
   */
  layout?: PromoTileLayout;
  /**
   * The alignment of the link/button.
   * 'start': Aligns to the start of the container
   * 'end': Aligns to the end of the container
   * @default 'end'
   * @type { 'start' | 'end' }
   */
  linkAlign?: LinkAlignment;
  /**
   * Tracking callbacks for analytics events
   */
  linkStyle?: {
    noPadding?: boolean;
  };
  tracking?: {
    viewed?: () => void;
    viewThreshold?: ZeroToOne;
  };
};

export function hasMultiThresholds(
  threshold: Threshold | [Threshold, Threshold, ...Threshold[]]
): threshold is [Threshold, Threshold, ...Threshold[]] {
  return !!(threshold && Array.isArray(threshold) && threshold.length > 1);
}

function generateProgressBarProps(progress: PromoTileProgress | undefined): ProgressBarProps | undefined {
  let progressBarProps: ProgressBarProps | undefined

  if (progress) {
    if (hasMultiThresholds(progress.threshold)) {

      const lastThreshold = progress.threshold[progress.threshold.length - 1]
      const max = lastThreshold.value;

      const ticks = progress.threshold.map((th) => {
        const thresholdValue = th.value

        const offset = (thresholdValue / max) * 100;

        const active = progress.current >= thresholdValue;

        const isGift = th.isGift || false;
        const reward = th.reward;
        const text = isGift ? undefined : reward;
        const title = isGift ? "Gift" : `${reward}`;

        const tick = {
          active: active,
          text: text,
          showIcon: isGift,
          title: title,
          offset: offset,
        }

        return tick
      })

      progressBarProps = {
        variant: "ticked",
        current: progress.current,
        max: max,
        ticks: ticks
      }
    } else {
      progressBarProps = {
        variant: "continuous",
        current: progress.current,
        max: progress.threshold.value
      }
    }
  }

  return progressBarProps
}

export const PromoTile = ({
  title,
  description,
  buttonText,
  onClick,
  href,
  target,
  image,
  progress,
  successIcon = false,
  linkAsButton = true,
  variant = "standard",
  layout = "padding",
  linkAlign = "end",
  linkStyle = {
    noPadding: false
  },
  tracking
}: PromoTileProps) => {
  const hasFooterContent = progress || buttonText;

  const progressBarProps = generateProgressBarProps(progress)

  const { inView, ref } = useInView({ threshold: Number(tracking?.viewThreshold ?? 1), triggerOnce: true });

  useEffect(() => {
    if (inView) {
      tracking?.viewed?.();
    }
  }, [inView]);

  return (
    <div className={styles.promoTileContainer({ variant, layout })} ref={ref}>
      {image && (
        <div className={styles.imageContainer({ variant, layout })}>
          <Image
            src={image?.src}
            alt={image?.altText || title}
            width={IMAGE_SIZE_OPTIONS[variant][layout]}
            height={IMAGE_SIZE_OPTIONS[variant][layout]}
            priority
            sizes="300px"
            objectFit="contain"
          />
        </div>
      )}

      <div className={styles.contentContainer({ variant, layout })}>
        <div className={styles.contentTitle}>
          <Typography variant="subheader-7">{title}</Typography>
          {successIcon && (
            <SuccessIcon
              data-testid="success-icon"
              className={styles.successIcon}
              aria-label="Success"
            />
          )}
        </div>

        <div className={styles.contentBody}>
          <Typography variant="smallprint-md" color="primary-medium">
            <Markdown>{description}</Markdown>
          </Typography>
        </div>

        {hasFooterContent && (
          <div className={styles.contentFooter}>
            {
              (progressBarProps && !buttonText &&
                <ProgressBar
                  {...progressBarProps}
                />
              )
            }
            {buttonText &&
              <div className={styles.linkContainer({ linkAlign })}>
                {linkAsButton ? (
                  <Button
                    variant="link"
                    size="small"
                    noPadding
                    onClick={onClick}
                    text={buttonText}
                  />
                ) : (
                  <Link
                    href={href || "#"}
                    target={target}
                    text={buttonText}
                    noPadding={linkStyle.noPadding}
                    size="small"
                    onClick={() => {
                      onClick?.();
                    }}
                  />
                )}
              </div>
            }
          </div>
        )}
      </div>
    </div>
  );
};
